'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react'

import { log } from '@src/utils/log.util'
import { normalizeURL } from '@src/utils/url.util'

interface URLContextType {
  /** The complete URL object */
  url: URL
  /** The pathname portion of the URL */
  pathname: string
  /** URLSearchParams object for easy query parameter access */
  searchParams: URLSearchParams
  /** The search string including the '?' prefix */
  search: string
  /** The hash portion of the URL including the '#' prefix */
  hash: string
  /** The host (hostname + port) */
  host: string
  /** The origin (protocol + host) */
  origin: string
  /** The complete URL as a string */
  href: string
}

const URLContext = createContext<URLContextType | null>(null)

interface URLProviderProps {
  children: ReactNode
  initialURL: string
}

/**
 * URLProvider provides isomorphic and synchronous access to the current URL.
 * It initializes with server-side URL data and updates automatically during client-side navigation.
 */
export function URLProvider({ children, initialURL }: URLProviderProps) {
  const [url, setUrl] = useState<URL>(() => normalizeURL(initialURL).url)

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { url: normalizedUrl, urlChanged } = normalizeURL(window.location.href)

      if (urlChanged) {
        log.info(`[URLProvider] Redirecting to normalized URL ${normalizedUrl.href}`)
        router.replace(normalizedUrl.href)
        return
      }

      setUrl(normalizedUrl)
    }
  }, [pathname, searchParams, router])

  const contextValue: URLContextType = {
    url,
    pathname: url.pathname,
    searchParams: new URLSearchParams(url.search),
    search: url.search,
    hash: url.hash,
    host: url.host,
    origin: url.origin,
    href: url.href,
  }

  return <URLContext.Provider value={contextValue}>{children}</URLContext.Provider>
}

export function useURL(): URLContextType {
  const context = useContext(URLContext)
  if (!context) {
    throw new Error('useURL must be used within URLProvider')
  }
  return context
}
